/**
 * QBridgeHelpModal Component (E11)
 * Comprehensive help modal with installation guide and troubleshooting
 *
 * Features:
 * - Step-by-step installation guide
 * - Troubleshooting FAQ accordion
 * - Link to AI help in chat
 * - Download button integration
 * - Matches platform styling
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ComputerIcon from '@mui/icons-material/Computer';
import EmailIcon from '@mui/icons-material/Email';
import SecurityIcon from '@mui/icons-material/Security';
import ChatIcon from '@mui/icons-material/Chat';
import LinkIcon from '@mui/icons-material/Link';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import QBridgeDownloadButton from './QBridgeDownloadButton';

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const COLORS = {
  background: '#0F0F14',
  surface: 'rgba(255, 255, 255, 0.02)',
  surfaceHover: 'rgba(255, 255, 255, 0.04)',
  border: 'rgba(255, 255, 255, 0.1)',
  borderLight: 'rgba(255, 255, 255, 0.08)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)',
  textMuted: 'rgba(255, 255, 255, 0.5)',
  primary: '#27B56D',
  primaryLight: 'rgba(39, 181, 109, 0.1)',
  primaryBorder: 'rgba(39, 181, 109, 0.3)',
  accent: '#7c3aed',
  accentLight: 'rgba(124, 58, 237, 0.1)',
  error: '#EF4444',
  warning: '#F59E0B'
};

const INSTALLATION_STEPS = [
  {
    icon: <DownloadIcon />,
    title: 'Download QBridge',
    description: 'Click the download button below to get QBridge for Windows',
    detail: 'QBridge is a small Windows app (15 MB) that reads your MT5 trades and sends them securely to Q AI.'
  },
  {
    icon: <FolderOpenIcon />,
    title: 'Find the installer',
    description: 'Open your Downloads folder and locate "QBridge-Setup.exe"',
    detail: 'The file should appear in your Downloads folder. If you saved it elsewhere, navigate to that location.'
  },
  {
    icon: <PlayArrowIcon />,
    title: 'Run the installer',
    description: 'Double-click to run. Click "Yes" if Windows asks for permission',
    detail: 'Windows may show a security prompt. This is normal for new apps. Click "Yes" or "Run anyway" to proceed.'
  },
  {
    icon: <ComputerIcon />,
    title: 'Keep MT5 running',
    description: 'Make sure MetaTrader 5 is open before connecting',
    detail: 'QBridge needs MT5 to be running to read your trade history. Open MT5 and log into your account.'
  },
  {
    icon: <EmailIcon />,
    title: 'Enter your Q AI email',
    description: 'In QBridge, enter the email you used to sign up for Q AI',
    detail: 'This links your trading data to your Q AI account. Make sure to use the same email address.'
  }
];

const TROUBLESHOOTING_FAQ = [
  {
    question: '"MT5 not found" error',
    answer: 'Make sure MetaTrader 5 is installed and running on your computer. QBridge looks for MT5 in the default installation location. If you installed MT5 to a custom folder, you may need to specify the path in QBridge settings.'
  },
  {
    question: '"Connection failed" error',
    answer: 'Check that: (1) You\'re using the same email as your Q AI account, (2) Your internet connection is working, (3) QBridge has firewall permission. Try closing and reopening QBridge.'
  },
  {
    question: 'Trades not showing in Q AI',
    answer: 'QBridge only imports closed trades. If you have no closed trades yet, there\'s nothing to import. Also, the initial sync may take a few minutes for large trade histories.'
  },
  {
    question: 'QBridge won\'t start',
    answer: 'Try: (1) Run as Administrator (right-click > Run as administrator), (2) Check your antivirus isn\'t blocking it, (3) Make sure you have Windows 10 or 11. If the problem persists, try reinstalling.'
  },
  {
    question: 'Windows security warning',
    answer: 'Windows may show "Windows protected your PC" for new software. Click "More info" then "Run anyway". This is normal for software that\'s not from the Microsoft Store.'
  },
  {
    question: 'How do I update QBridge?',
    answer: 'QBridge checks for updates automatically. When an update is available, you\'ll see a notification in the app. Click to download and install the new version.'
  }
];

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * QBridgeHelpModal
 * @param {boolean} open - Modal visibility
 * @param {function} onClose - Close handler
 * @param {function} onAskHelp - Handler to trigger AI help in chat
 * @param {number} initialTab - Starting tab (0 = Guide, 1 = Troubleshoot)
 */
const QBridgeHelpModal = ({
  open = false,
  onClose,
  onAskHelp,
  initialTab = 0
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleFaqChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : null);
  };

  const handleAskHelp = () => {
    onAskHelp?.();
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: COLORS.background,
          backgroundImage: 'none',
          border: `1px solid ${COLORS.border}`,
          borderRadius: '16px',
          maxWidth: '520px',
          maxHeight: '85vh'
        }
      }}
    >
      {/* ════════════════════════════════════════════════════════════════════════
          HEADER
          ════════════════════════════════════════════════════════════════════════ */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 24px 12px',
          borderBottom: `1px solid ${COLORS.borderLight}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              backgroundColor: COLORS.accentLight,
              border: `1px solid rgba(124, 58, 237, 0.2)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <HelpOutlineIcon sx={{ color: COLORS.accent, fontSize: '1.25rem' }} />
          </Box>
          <Box>
            <Typography
              sx={{
                color: COLORS.text,
                fontSize: '1.125rem',
                fontWeight: 600,
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
              }}
            >
              Q AI Help Center
            </Typography>
            <Typography
              sx={{
                color: COLORS.textMuted,
                fontSize: '0.8125rem'
              }}
            >
              Connect MT5 with QBridge
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            color: COLORS.textMuted,
            '&:hover': {
              color: COLORS.text,
              backgroundColor: COLORS.surfaceHover
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* ════════════════════════════════════════════════════════════════════════
          TABS
          ════════════════════════════════════════════════════════════════════════ */}
      <Box sx={{ borderBottom: `1px solid ${COLORS.borderLight}` }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              color: COLORS.textMuted,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              minHeight: 44,
              '&.Mui-selected': {
                color: COLORS.primary
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: COLORS.primary,
              height: 2
            }
          }}
        >
          <Tab
            icon={<LinkIcon sx={{ fontSize: '1.125rem' }} />}
            iconPosition="start"
            label="Setup Guide"
          />
          <Tab
            icon={<ErrorOutlineIcon sx={{ fontSize: '1.125rem' }} />}
            iconPosition="start"
            label="Troubleshoot"
          />
        </Tabs>
      </Box>

      {/* ════════════════════════════════════════════════════════════════════════
          CONTENT
          ════════════════════════════════════════════════════════════════════════ */}
      <DialogContent sx={{ padding: 0 }}>
        {/* Tab 0: Setup Guide */}
        {activeTab === 0 && (
          <Box sx={{ padding: '20px 24px' }}>
            {/* Download Button */}
            <Box sx={{ mb: 3 }}>
              <QBridgeDownloadButton variant="secondary" showDetails={true} />
            </Box>

            <Divider sx={{ borderColor: COLORS.borderLight, mb: 3 }} />

            {/* Installation Steps */}
            <Typography
              sx={{
                color: COLORS.text,
                fontSize: '0.9375rem',
                fontWeight: 600,
                mb: 2
              }}
            >
              Installation Steps
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {INSTALLATION_STEPS.map((step, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      backgroundColor: COLORS.primaryLight,
                      border: `1px solid ${COLORS.primaryBorder}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <Typography
                      sx={{
                        color: COLORS.primary,
                        fontSize: '0.875rem',
                        fontWeight: 700
                      }}
                    >
                      {index + 1}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        color: COLORS.text,
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        mb: 0.25
                      }}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: COLORS.textSecondary,
                        fontSize: '0.8125rem',
                        mb: 0.5
                      }}
                    >
                      {step.description}
                    </Typography>
                    <Typography
                      sx={{
                        color: COLORS.textMuted,
                        fontSize: '0.75rem',
                        fontStyle: 'italic'
                      }}
                    >
                      {step.detail}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Security Info */}
            <Box
              sx={{
                mt: 3,
                padding: '12px 16px',
                backgroundColor: COLORS.primaryLight,
                border: `1px solid ${COLORS.primaryBorder}`,
                borderRadius: '10px'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <SecurityIcon sx={{ color: COLORS.primary, fontSize: '1rem' }} />
                <Typography
                  sx={{
                    color: COLORS.text,
                    fontSize: '0.8125rem',
                    fontWeight: 600
                  }}
                >
                  Your data is safe
                </Typography>
              </Box>
              <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.75rem' }}>
                QBridge runs locally on your PC. It only reads trade history (read-only) and never
                accesses your MT5 password. Data is encrypted in transit.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Tab 1: Troubleshooting */}
        {activeTab === 1 && (
          <Box sx={{ padding: '20px 24px' }}>
            <Typography
              sx={{
                color: COLORS.text,
                fontSize: '0.9375rem',
                fontWeight: 600,
                mb: 2
              }}
            >
              Common Issues
            </Typography>

            <Box sx={{ mb: 3 }}>
              {TROUBLESHOOTING_FAQ.map((faq, index) => (
                <Accordion
                  key={index}
                  expanded={expandedFaq === index}
                  onChange={handleFaqChange(index)}
                  disableGutters
                  elevation={0}
                  sx={{
                    backgroundColor: 'transparent',
                    border: `1px solid ${COLORS.borderLight}`,
                    borderRadius: '8px !important',
                    mb: 1,
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      borderColor: COLORS.primary,
                      backgroundColor: COLORS.surface
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: COLORS.textMuted }} />}
                    sx={{
                      minHeight: 48,
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0'
                      }
                    }}
                  >
                    <Typography
                      sx={{
                        color: expandedFaq === index ? COLORS.text : COLORS.textSecondary,
                        fontSize: '0.875rem',
                        fontWeight: 500
                      }}
                    >
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                    <Typography
                      sx={{
                        color: COLORS.textMuted,
                        fontSize: '0.8125rem',
                        lineHeight: 1.6
                      }}
                    >
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            {/* AI Help CTA */}
            <Box
              sx={{
                padding: '16px',
                backgroundColor: COLORS.accentLight,
                border: `1px solid rgba(124, 58, 237, 0.2)`,
                borderRadius: '12px',
                textAlign: 'center'
              }}
            >
              <ChatIcon sx={{ color: COLORS.accent, fontSize: '1.5rem', mb: 1 }} />
              <Typography
                sx={{
                  color: COLORS.text,
                  fontSize: '0.9375rem',
                  fontWeight: 600,
                  mb: 0.5
                }}
              >
                Still need help?
              </Typography>
              <Typography
                sx={{
                  color: COLORS.textMuted,
                  fontSize: '0.8125rem',
                  mb: 2
                }}
              >
                Ask Q for personalized assistance — it's FREE for installation help!
              </Typography>
              <Button
                onClick={handleAskHelp}
                fullWidth
                startIcon={<ChatIcon />}
                sx={{
                  background: `linear-gradient(135deg, ${COLORS.accent} 0%, #a855f7 100%)`,
                  color: '#FFFFFF',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  '&:hover': {
                    background: `linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)`
                  }
                }}
              >
                Ask Q for Help
              </Button>
              <Typography
                sx={{
                  color: COLORS.textMuted,
                  fontSize: '0.75rem',
                  mt: 1,
                  fontStyle: 'italic'
                }}
              >
                Type "Help me install QBridge" in the chat
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QBridgeHelpModal;
